import React, { useState, useEffect, useCallback } from 'react';
import { getEmailLogs, downloadEmailLogs, getCampaigns, checkBounces } from '../api';
import toast from 'react-hot-toast';
import { Mail, Download, Search, X, Filter, RefreshCw, AlertTriangle } from 'lucide-react';

const STATUSES = [
  { key: '', label: 'All', color: '' },
  { key: 'sent', label: 'Sent', color: 'var(--info)' },
  { key: 'delivered', label: 'Delivered', color: 'var(--ok)' },
  { key: 'failed', label: 'Failed', color: 'var(--err)' },
  { key: 'bounced', label: 'Bounced', color: 'var(--err)' },
  { key: 'replied', label: 'Replied', color: 'var(--reply)' },
  { key: 'pending', label: 'Pending', color: 'var(--warn)' },
];

export default function EmailLogs() {
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [campaigns, setCampaigns] = useState([]);
  const [checking, setChecking] = useState(false);

  // Filters
  const [status, setStatus] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    try {
      const params = { page, limit: 100 };
      if (status) params.status = status;
      if (campaignId) params.campaignId = campaignId;
      if (search) params.search = search;
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const r = (await getEmailLogs(params)).data;
      setEmails(r.emails); setTotal(r.total); setPages(r.pages);
    } catch (e) {}
  }, [page, status, campaignId, search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getCampaigns().then(r => setCampaigns(r.data)).catch(() => {});
  }, []);

  const handleDownload = () => {
    const params = {};
    if (status) params.status = status;
    if (campaignId) params.campaignId = campaignId;
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    downloadEmailLogs(params);
    toast.success('Downloading CSV...');
  };

  const handleCheckBounces = async () => {
    setChecking(true);
    try {
      const r = (await checkBounces()).data;
      toast.success(r.message);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Bounce check failed'); }
    setChecking(false);
  };

  const clearFilters = () => {
    setStatus(''); setCampaignId(''); setSearch(''); setDateFrom(''); setDateTo(''); setPage(1);
  };

  const hasFilters = status || campaignId || search || dateFrom || dateTo;

  return (<>
    <div className="pg-hdr">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2>Email Logs</h2>
          <p>{total} total emails {hasFilters && '(filtered)'}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-s" onClick={handleCheckBounces} disabled={checking}>
            <AlertTriangle size={15} /> {checking ? 'Scanning...' : 'Detect Bounces (IMAP)'}
          </button>
          <button className="btn btn-p" onClick={handleDownload}>
            <Download size={15} /> Download CSV
          </button>
          <button className="btn btn-g" onClick={load}><RefreshCw size={15} /></button>
        </div>
      </div>
    </div>

    <div className="pg-body">
      {/* Status Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
        {STATUSES.map(s => (
          <button key={s.key}
            className={`btn btn-sm ${status === s.key ? 'btn-p' : 'btn-g'}`}
            onClick={() => { setStatus(s.key); setPage(1); }}
            style={status === s.key && s.color ? { background: s.color, borderColor: s.color } : {}}>
            {s.label}
            {s.key && <span style={{ marginLeft: 4, opacity: .7 }}>
              {emails.length > 0 || s.key === status ? '' : ''}
            </span>}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 14, padding: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, alignItems: 'end' }}>
          <div>
            <label className="lbl">Search</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Search size={14} style={{ color: 'var(--txtm)' }} />
              <input className="inp" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Email, name, company..." style={{ border: 'none', background: 'transparent', padding: 0 }} />
            </div>
          </div>
          <div><label className="lbl">Campaign</label>
            <select className="inp sel" value={campaignId} onChange={e => { setCampaignId(e.target.value); setPage(1); }} style={{ fontSize: 12, padding: '7px 10px' }}>
              <option value="">All Campaigns</option>
              {campaigns.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          <div><label className="lbl">From</label><input className="inp" type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} style={{ fontSize: 12, padding: '7px 10px' }} /></div>
          <div><label className="lbl">To</label><input className="inp" type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} style={{ fontSize: 12, padding: '7px 10px' }} /></div>
          {hasFilters && <div><button className="btn btn-g btn-sm" onClick={clearFilters}>Clear All</button></div>}
        </div>
      </div>

      {/* Download buttons per status */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--txtm)', lineHeight: '28px' }}>Quick Download:</span>
        {[{ s: 'sent', l: '📤 Sent', c: 'var(--info)' }, { s: 'delivered', l: '✅ Delivered', c: 'var(--ok)' }, { s: 'failed', l: '❌ Failed', c: 'var(--err)' }, { s: 'bounced', l: '⚠️ Bounced', c: 'var(--err)' }, { s: 'replied', l: '💬 Replied', c: 'var(--reply)' }].map(item => (
          <button key={item.s} className="btn btn-g btn-sm" onClick={() => { downloadEmailLogs({ status: item.s, campaignId }); toast.success(`Downloading ${item.s}...`); }}>
            <Download size={12} /> {item.l}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="card">
        {emails.length === 0 ? (
          <div className="empty"><Mail size={40} /><h3>No emails found</h3><p>Adjust filters or send a campaign first</p></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th>Campaign</th>
                  <th>Sent At</th>
                  <th>Error / Response</th>
                </tr>
              </thead>
              <tbody>
                {emails.map(e => (
                  <tr key={e._id}>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{e.to}</td>
                    <td>{e.toName || '—'}</td>
                    <td>{e.toCompany || '—'}</td>
                    <td><span className={`badge b-${e.status}`}>{e.status}</span></td>
                    <td style={{ fontSize: 11, color: 'var(--txt2)' }}>{e.campaignId?.name || '—'}</td>
                    <td style={{ fontSize: 11, color: 'var(--txtm)' }}>{e.sentAt ? new Date(e.sentAt).toLocaleString() : '—'}</td>
                    <td style={{ fontSize: 10.5, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: e.error ? 'var(--err)' : 'var(--txtm)' }} title={e.error || e.smtpResponse || ''}>
                      {e.error || e.smtpResponse || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 14, alignItems: 'center' }}>
            <button className="btn btn-g btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={{ fontSize: 11, color: 'var(--txtm)' }}>Page {page} of {pages} ({total} total)</span>
            <button className="btn btn-g btn-sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>
    </div>
  </>);
}
